import React, { JSX, useEffect, useRef, useState } from "react";
import "./enhanced-sources.scss";
import { useDispatch, useSelector } from "react-redux";
import { closeSources, openSources } from "../../state/slice";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import useScreenSize from "../../hooks/useScreenSize";
import ReactPlayer from "react-player";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

// Define TypeScript interfaces
interface ContentDelta {
  title?: string;
  source_type?: string;
  source_snippet_link?: string;
  full_source_link?: string;
  content?: string;
  number?: number;
  year?: string | number;
  page?: string | number;
  respondents?: string | number;
  thumbnail_url?: string;
}

interface SourceItem {
  content_delta: ContentDelta;
}

interface RootState {
  sourcesOpen: boolean;
  // Add other state properties as needed
}

interface MetadataObject {
  year?: string;
  page?: string;
  timestamp?: string;
  respondents?: string | number;
}

interface SourceTitleInfo {
  displayTitle: string;
  metadata: MetadataObject;
}

interface SourceBadgeInfo {
  text: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
  icon: JSX.Element;
}

interface VideoParams {
  time: string;
  autoPlay: boolean;
}

/**
 * Enhanced Sources Component
 * Displays source materials for answered questions
 * with improved UI/UX and mobile optimization
 */

const ZoomableImage = ({ url, alt }: { url: string; alt: string }) => {
  const transformWrapperRef = useRef(null);

  return (
    <TransformWrapper ref={transformWrapperRef}>
      <TransformComponent>
        <img src={url} alt={alt} className="fullscreen-image" />
      </TransformComponent>
    </TransformWrapper>
  );
};

const EnhancedSources = (props: { sources?: SourceItem[] }) => {
  // Props and state
  const sources = props?.sources || [];
  const dispatch = useDispatch();
  const sourcesOpen = useSelector((state: RootState) => state.sourcesOpen);
  const { isMobile } = useScreenSize();
  // const { zoomIn, zoomOut, resetTransform } = useControls();

  // State for expanded sections and fullscreen content
  const [expandedSections, setExpandedSections] = useState<number[]>([]);
  const [isFullscreenContent, setIsFullscreenContent] =
    useState<boolean>(false);
  const [fullscreenContent, setFullscreenContent] = useState<SourceItem | null>(
    null
  );
  const [fullscreenType, setFullscreenType] = useState<string>("");

  // Refs for media players
  const playerRef = useRef<any>(null);
  const [currentlyPlayingIndex, setCurrentlyPlayingIndex] = useState(null);

  const playerRef1 = useRef<(ReactPlayer | null)[]>([]);

  // Assets
  // const logo = require("../../assets/submit-ico.svg")?.default;
  const mainlogo = require("../../assets/world-cc-logo-only.ico");
  // const pinLogo = require("../../assets/link.svg")?.default;
  // const expandLogo = require("../../assets/arrows-angle-expand.svg")?.default;

  // Reset state when sources change
  useEffect(() => {
    try {
      closeSourcesPanel();
      setExpandedSections([]);
      setIsFullscreenContent(false);
      setFullscreenContent(null);
      setCurrentlyPlayingIndex(null);
    } catch (error) {
      console.log("error==>", error);
    }
  }, [sources]);

  // Handle keyboard events for accessibility
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isFullscreenContent) {
        handleCloseFullscreen();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullscreenContent]);

  // Open sources panel
  const openSourcePanel = () => {
    dispatch(openSources());
    // Add body class to handle main content shifting
    // document.body.classList.add('sources-panel-open');
  };

  // Close sources panel
  const closeSourcesPanel = () => {
    dispatch(closeSources());
    collapseAll();
    // Remove body class when panel closes
    // document.body.classList.remove('sources-panel-open');
  };

  // Toggle section expansion
  const toggleSection = (index: number, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }

    if (expandedSections.includes(index)) {
      setExpandedSections(expandedSections.filter((i) => i !== index));
    } else {
      setExpandedSections([...expandedSections, index]);
    }
  };

  // Check if a section is expanded
  const isExpanded = (index: number) => expandedSections.includes(index);

  // Handle fullscreen content display
  const handleExpandClick = (
    mode: string,
    data: SourceItem,
    e?: React.MouseEvent
  ) => {
    if (e) {
      e.stopPropagation();
    }
    setFullscreenType(mode);
    setFullscreenContent(data);
    setIsFullscreenContent(true);
    setCurrentlyPlayingIndex(null);
  };

  // Close fullscreen display
  const handleCloseFullscreen = () => {
    setIsFullscreenContent(false);
    setTimeout(() => {
      setFullscreenContent(null);
    }, 300); // Wait for animation to complete
  };

  // Handle external link clicks
  // const handleLinkClick = (e: React.MouseEvent, data: SourceItem) => {
  //   e.stopPropagation();
  //   if (data?.content_delta?.full_source_link) {
  //     const url = data?.content_delta?.full_source_link;
  //     window.open(url, "_blank");
  //   }
  // };

  const openLinkInNewTab = (url: string) => {
    window.open(url, "_blank");
  };

  // Seek video to specific time
  const handleReady = (time: string) => {
    if (playerRef.current) {
      playerRef.current.seekTo(Number(time), "seconds");
    }
  };

  const handleReadySmall = (playerRef1, time, i) => {
    console.log("handleready event", playerRef1, time, i);
    // Seek to 60 seconds once the player is ready
    if (playerRef1?.current[i]) {
      // console.log("current value for",playerRef)
      playerRef1.current[i].seekTo(Number(time), "seconds");
    }
  };

 const handlePlayerClick = (event, index) => {
    if (event.target.closest('.video-thumbnail-container') || event.target.closest('.video-overlay')|| event.target.closest('.fullscreen-video-container')|| event.target.closest('.fullscreen-video')) {
      if (currentlyPlayingIndex !== null && currentlyPlayingIndex !== index) {
        playerRef1.current[currentlyPlayingIndex].getInternalPlayer().pause();
      }
      setCurrentlyPlayingIndex(currentlyPlayingIndex === index ? null : index);
    }
  };
  // Parse URL for video parameters
  const parseUrl = (url?: string): VideoParams => {
    if (!url) return { time: "0", autoPlay: false };

    const params = new URLSearchParams(url.split("?")[1] || "");
    const time = params.get("time") || params.get("wtime") || "0";
    const autoPlay = params.get("autoPlay") === "true";
    return { time, autoPlay };
  };

  // Collapse all expanded sections
  const collapseAll = () => {
    setExpandedSections([]);
  };

  // Parse source title to extract relevant information
  const parseSourceTitle = (
    title: string,
    sourceType?: string
  ): SourceTitleInfo => {
    if (!title) return { displayTitle: "Source", metadata: {} };

    // Remove position prefix if present
    let cleanTitle = title.replace(/^\s*\[\d+\]:\s*/, "");

    // Remove relevance score information
    cleanTitle = cleanTitle.replace(/Relevance=[\d.]+\s*\.?/i, "");

    // Extract metadata
    const metadata: MetadataObject = {};

    // Extract year
    const yearMatch = cleanTitle.match(/Year\s*-\s*(\d{4}|Off)/i);
    if (yearMatch) {
      metadata.year = yearMatch[1];
      cleanTitle = cleanTitle.replace(/Year\s*-\s*(\d{4}|Off)/i, "");
    }

    // Extract page number
    const pageMatch = cleanTitle.match(/Page\s*-\s*(\d+)/i);
    if (pageMatch) {
      metadata.page = pageMatch[1];
      cleanTitle = cleanTitle.replace(/Page\s*-\s*(\d+)/i, "");
    }

    // Extract timestamp for webinars
    const timestampMatch = cleanTitle.match(/Timestamp\s*-\s*([\d:]+)/i);
    if (timestampMatch) {
      metadata.timestamp = timestampMatch[1];
      cleanTitle = cleanTitle.replace(/Timestamp\s*-\s*([\d:]+)/i, "");
    }

    // Clean up the title (additional cleaning logic from original code)
    if (
      sourceType?.toLowerCase() === "webinar" ||
      sourceType?.toLowerCase() === "podcast"
    ) {
      cleanTitle = cleanTitle.replace(/Section:.*?(,|$)/i, "");
      cleanTitle = cleanTitle.replace(/to\s+(\d{2}:\d{2}:\d{2})/i, "");
    }

    if (
      sourceType?.toLowerCase() === "survey" ||
      sourceType?.toLowerCase() === "survey data" ||
      sourceType?.toLowerCase() === "data"
    ) {
      cleanTitle = cleanTitle.replace(/by\s+Region\s+in\s+year\s+\d{4}/i, "");
    }

    cleanTitle = cleanTitle
      .replace(/^Webinar\s+Source\s*-\s*/i, "")
      .replace(/^Survey Data\s+Source\s*-\s*/i, "")
      .replace(/^Source\s*-\s*/i, "");

    cleanTitle = cleanTitle.replace(/[,.\s]+$/, "");

    while (cleanTitle.includes(",,")) {
      cleanTitle = cleanTitle.replace(/,,/g, ",");
    }

    cleanTitle = cleanTitle.replace(/,$/, "");
    cleanTitle = cleanTitle.trim();

    cleanTitle = cleanTitle
      .replace(/^\*+|\*+$/g, "") // remove starting/ending asterisks
      .trim()
      .replace(/,+/g, ",") // replace multiple commas with one
      .replace(/\s*,\s*/g, ", ") // normalize spacing
      .replace(/(,\s*)+$/, ""); // remove trailing commas

    return {
      displayTitle: cleanTitle,
      metadata,
    };
  };

  // Get source type badge info
  const getSourceTypeBadge = (sourceType?: string): SourceBadgeInfo => {
    const type = (sourceType || "document")?.toLowerCase();

    // WorldCC color scheme
    const primaryColor = "#0073B5"; // Primary WorldCC blue
    const secondaryColor = "#004070"; // Darker WorldCC blue

    if (type === "webinar" || type === "podcast") {
      return {
        text: type.charAt(0).toUpperCase() + type.slice(1),
        bgColor: "#e0f0ff", // Light blue background
        textColor: primaryColor,
        borderColor: primaryColor,
        icon: (
          <svg
            className="h-3 w-3 fill-current"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        ),
      };
    } else if (
      type === "report" ||
      type === "checklist" ||
      type === "slides" ||
      type === "blog" ||
      type === "other" ||
      type === "definition"
    ) {
      return {
        text: type.charAt(0).toUpperCase() + type.slice(1),
        bgColor: "#e5f0f7", // Light blue background
        textColor: secondaryColor,
        borderColor: secondaryColor,
        icon: (
          <svg
            className="h-3 w-3 stroke-current"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
          >
            <path
              d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <polyline
              points="14 2 14 8 20 8"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <line
              x1="16"
              y1="13"
              x2="8"
              y2="13"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <line
              x1="16"
              y1="17"
              x2="8"
              y2="17"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <polyline
              points="10 9 9 9 8 9"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      };
    } else if (type === "survey" || type === "survey data" || type === "data") {
      return {
        text: "Survey Data",
        bgColor: "#e5f0f7", // Light teal background
        textColor: secondaryColor, // Dark teal
        borderColor: secondaryColor,
        icon: (
          <svg
            className="h-3 w-3 stroke-current"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
          >
            <path
              d="M18 20V10"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 20V4"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6 20v-6"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      };
    } else {
      return {
        text: "Document",
        bgColor: "#f0f0f5", // Light gray-blue background
        textColor: "#505A64", // Dark gray
        borderColor: "#505A64",
        icon: (
          <svg
            className="h-3 w-3 stroke-current"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
          >
            <path
              d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <polyline
              points="14 2 14 8 20 8"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <line
              x1="16"
              y1="13"
              x2="8"
              y2="13"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <line
              x1="16"
              y1="17"
              x2="8"
              y2="17"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <polyline
              points="10 9 9 9 8 9"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      };
    }
  };

  // Get the metadata string for an expanded source content section
  // const getExpandedSourceMetadata = (data: SourceItem): string => {
  //   const type = (data?.content_delta?.source_type || "document")?.toLowerCase();
  //   const parsedTitle = parseSourceTitle(data?.content_delta?.title || "", type);

  //   if (type === "webinar" || type === "podcast") {
  //     const { time } = parseUrl(
  //       data?.content_delta?.source_snippet_link ||
  //         data?.content_delta?.full_source_link
  //     );
  //     const timeNum = Number(time);

  //     // Format time as MM:SS
  //     const minutes = Math.floor(timeNum / 60);
  //     const seconds = Math.floor(timeNum % 60);
  //     const formattedTime = `${minutes}:${seconds.toString().padStart(2, "0")}`;

  //     // Calculate end time (add 5 minutes as an example)
  //     const endMinutes = Math.floor((timeNum + 300) / 60);
  //     const endSeconds = Math.floor((timeNum + 300) % 60);
  //     const formattedEndTime = `${endMinutes}:${endSeconds
  //       .toString()
  //       .padStart(2, "0")}`;

  //     return `Time: ${formattedTime}-${formattedEndTime}`;
  //   } else if (type === "survey" || type === "survey data" || type === "data") {
  //     const respondents = data?.content_delta?.respondents || parsedTitle.metadata.respondents || null;
  //     return respondents ? `${respondents} Respondents` : "";
  //   } else {
  //     const page = parsedTitle.metadata.page || data?.content_delta?.page || null;
  //     return page ? `Page ${page}` : "";
  //   }
  // };

  // Get the metadata string for a source
  const getSourceMetadata = (data: SourceItem): string => {
    const type = (
      data?.content_delta?.source_type || "document"
    )?.toLowerCase();
    const parsedTitle = parseSourceTitle(
      data?.content_delta?.title || "",
      type
    );
    const year =
      parsedTitle.metadata.year ||
      data?.content_delta?.year ||
      new Date().getFullYear().toString();

    if (type === "webinar" || type === "podcast") {
      const { time } = parseUrl(
        data?.content_delta?.source_snippet_link ||
          data?.content_delta?.full_source_link
      );
      const timeNum = Number(time);

      // Format time as MM:SS
      const minutes = Math.floor(timeNum / 60);
      const seconds = Math.floor(timeNum % 60);
      const formattedTime = `${minutes}:${seconds.toString().padStart(2, "0")}`;

      // Calculate end time (add 5 minutes as an example)
      const endMinutes = Math.floor((timeNum + 300) / 60);
      const endSeconds = Math.floor((timeNum + 300) % 60);
      const formattedEndTime = `${endMinutes}:${endSeconds
        .toString()
        .padStart(2, "0")}`;

      return `Time ${formattedTime}-${formattedEndTime} • ${year}`;
    } else if (type === "survey" || type === "survey data" || type === "data") {
      const respondents =
        data?.content_delta?.respondents ||
        parsedTitle.metadata.respondents ||
        null;
      return respondents ? `${respondents} Respondents • ${year}` : `${year}`;
    } else {
      const page =
        parsedTitle.metadata.page || data?.content_delta?.page || null;
      return page ? `Page ${page} • ${year}` : `${year}`;
    }
  };

  // Render source snippet based on type
  const renderSourceSnippet = (data: SourceItem, index: number) => {
    const sourceType = data?.content_delta?.source_type?.toLowerCase();

    if (sourceType === "podcast" || sourceType === "webinar") {
      return renderVideoSnippet(data, index);
    } else if (data?.content_delta?.source_snippet_link) {
      return renderImageSnippet(data);
    } else {
      return renderTextSnippet(data);
    }
  };

  // Render video snippet with new media hint
  const renderVideoSnippet = (data: SourceItem, index: number) => {
    const url =
      data.content_delta?.source_snippet_link ||
      data.content_delta?.full_source_link;
    const { time } = parseUrl(url);
    const thumbnailUrl = data.content_delta?.thumbnail_url || "";

    // Format time as MM:SS
    const timeNum = Number(time);
    const minutes = Math.floor(timeNum / 60);
    const seconds = Math.floor(timeNum % 60);
    const formattedTime = `${minutes}:${seconds.toString().padStart(2, "0")}`;

    const sourceBadge = getSourceTypeBadge(data?.content_delta?.source_type);
    // const metadataText = getSourceMetadata(data);

    return (
      <div className="media-wrapper video-wrapper">
        <div className="source-metadata-container">
          <div className="metadata-left">
            <div
              className="source-badge"
              style={{
                backgroundColor: sourceBadge.bgColor,
                color: sourceBadge.textColor,
                borderColor: sourceBadge.borderColor,
              }}
            >
              <span className="badge-icon">{sourceBadge.icon}</span>
              <span>{sourceBadge.text}</span>
            </div>
          </div>

          {/* <div className="metadata-center">
            <span className="source-timestamp">{getExpandedSourceMetadata(data)}</span>
          </div> */}

          <div className="metadata-right">
            <button
              className="external-link-button"
              title="Open Full Webinar"
              onClick={(e) => {
                e.stopPropagation();
                if (url) window.open(url, "_blank");
              }}
            >
              <svg
                className="external-link-icon"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                width="16px"
                height="16px"
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg>
              {/* Source */}
              Open Full Webinar
            </button>
          </div>
        </div>

        <div
          className="video-thumbnail-container"
          onClick={(event) => handlePlayerClick(event, index)}
        >
          <div className="video-overlay">
            {/* <div className="play-button-container">
              <svg
                className="play-icon"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
            <p className="overlay-text">
              {isMobile ? "Tap to play video" : "Click to play video"}
            </p> */}
            {renderSmallscreenVideo(data, index)}
          </div>

          <img
            src={thumbnailUrl}
            alt={`${
              parseSourceTitle(
                data?.content_delta?.title || "",
                data?.content_delta?.source_type
              ).displayTitle || "Video"
            } thumbnail`}
            className="video-thumbnail"
          />

          {/* Time indicator */}
          <div className="video-time-indicator">{formattedTime}</div>
        </div>

        {/* Added media hint below video */}
        <div
          className="media-hint"
          onClick={(e) => handleExpandClick("video", data, e)}
        >
          {isMobile
            ? "Tap here to view in fullscreen"
            : "Click here to view in fullscreen"}
        </div>
      </div>
    );
  };

  // Render image snippet with new media hint
  const renderImageSnippet = (data: SourceItem) => {
    const sourceBadge = getSourceTypeBadge(data?.content_delta?.source_type);
    // const metadataText = getSourceMetadata(data);

    return (
      <div className="media-wrapper image-wrapper">
        {/* Metadata visibility - only when expanded */}
        <div className="source-metadata-container">
          <div className="metadata-left">
            <div
              className="source-badge"
              style={{
                backgroundColor: sourceBadge.bgColor,
                color: sourceBadge.textColor,
                borderColor: sourceBadge.borderColor,
              }}
            >
              {sourceBadge.icon}
              <span className="badge-text">{sourceBadge.text}</span>
            </div>
          </div>

          {/* <div className="metadata-center">
            <span className="source-page">{getExpandedSourceMetadata(data)}</span>
          </div> */}

          <div className="metadata-right">
            {data?.content_delta?.full_source_link && (
              <button
                className="external-link-button"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(data?.content_delta?.full_source_link, "_blank");
                }}
              >
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
                Open Full Source
              </button>
            )}
          </div>
        </div>

        <div
          className="image-container"
          onClick={(e) => handleExpandClick("image", data, e)}
        >
          <div className="image-overlay">
            <div className="zoom-icon-container">
              <svg
                className="zoom-icon"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                <line x1="11" y1="8" x2="11" y2="14"></line>
                <line x1="8" y1="11" x2="14" y2="11"></line>
              </svg>
            </div>
            <p className="overlay-text">
              {isMobile ? "Tap to enlarge" : "Click to view full size"}
            </p>
          </div>

          <img
            className="source-image"
            src={data?.content_delta?.source_snippet_link}
            alt={
              parseSourceTitle(
                data?.content_delta?.title || "",
                data?.content_delta?.source_type
              ).displayTitle || "Source image"
            }
          />
        </div>

        {/* Added media hint below image */}
        <div className="media-hint">
          {isMobile
            ? "Tap on the image to view in fullscreen"
            : "Click on the image to view in fullscreen"}
        </div>
      </div>
    );
  };

  // Render text snippet with new media hint
  const renderTextSnippet = (data: SourceItem) => {
    const sourceBadge = getSourceTypeBadge(data?.content_delta?.source_type);

    return (
      <div className="media-wrapper text-content-wrapper">
        {/* Metadata visibility - only when expanded */}
        <div className="source-metadata-container">
          <div className="metadata-left">
            <div
              className="source-badge"
              style={{
                backgroundColor: sourceBadge.bgColor,
                color: sourceBadge.textColor,
                borderColor: sourceBadge.borderColor,
              }}
            >
              <span className="badge-icon">{sourceBadge.icon}</span>
              <span className="badge-text">{sourceBadge.text}</span>
            </div>
          </div>

          {/* <div className="metadata-center" >
            <span className="source-page">{getExpandedSourceMetadata(data)}</span>
          </div> */}
        </div>

        <div
          className="text-preview-container"
          onClick={(e) => handleExpandClick("content", data, e)}
        >
          <div className="markdown-styles">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {data?.content_delta?.content || ""}
            </ReactMarkdown>
          </div>

          {/* Gradient overlay to indicate more content */}
          <div className="content-fade-gradient"></div>

          {/* View full content button */}
          <button className="view-full-content-button">
            <svg
              className="zoom-icon"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              <line x1="11" y1="8" x2="11" y2="14"></line>
              <line x1="8" y1="11" x2="14" y2="11"></line>
            </svg>
            View Full Content
          </button>
        </div>

        {/* Added media hint below text */}
        <div className="media-hint">
          {isMobile ? "Tap to view full content" : "Click to view full content"}
        </div>
      </div>
    );
  };

  // Enhanced fullscreen modal with improved header and mobile support
  const renderFullscreenModal = () => {
    if (!fullscreenContent) return null;

    let modalContent;
    const parsedTitle = parseSourceTitle(
      fullscreenContent?.content_delta?.title || "",
      fullscreenContent?.content_delta?.source_type
    );
    const modalTitle = parsedTitle.displayTitle || "Source";
    const sourceBadge = getSourceTypeBadge(
      fullscreenContent?.content_delta?.source_type
    );
    const metadata = getSourceMetadata(fullscreenContent);

    if (fullscreenType === "video") {
      modalContent = renderFullscreenVideo(fullscreenContent);
    } else if (fullscreenType === "image") {
      modalContent = renderFullscreenImage(fullscreenContent);
    } else {
      modalContent = renderFullscreenContent(fullscreenContent);
    }

    const isFullSourceLinkAvailable =
      !(
        fullscreenContent?.content_delta?.source_type?.toLowerCase() ===
          "survey" ||
        fullscreenContent?.content_delta?.source_type?.toLowerCase() ===
          "survey data"
      ) &&
      fullscreenContent?.content_delta?.full_source_link !== "" &&
      fullscreenContent?.content_delta?.full_source_link !== undefined &&
      fullscreenContent?.content_delta?.full_source_link != null
        ? true
        : false;

    const linkToOpen = fullscreenContent?.content_delta?.full_source_link;
    console.log(isFullSourceLinkAvailable, linkToOpen);
    return (
      <div
        className={`fullscreen-modal ${fullscreenType} ${
          isFullscreenContent ? "show" : ""
        }`}
        onClick={handleCloseFullscreen}
        aria-modal="true"
        role="dialog"
      >
        <div
          className="fullscreen-modal-content"
          onClick={(e) => e.stopPropagation()}
          aria-labelledby="fullscreen-modal-title"
        >
          <div
            className="fullscreen-modal-header"
            style={{ padding: "9px 16px" }}
          >
            <div className="fullscreen-modal-title">
              <h3 id="fullscreen-modal-title">{modalTitle}</h3>
            </div>

            <div className="fullscreen-modal-actions">
              {/* Consistent close button styling with mobile sources panel */}
              <button
                className="fullscreen-close-button"
                onClick={handleCloseFullscreen}
                aria-label="Close fullscreen view"
                title="Close"
              >
                {/* <svg viewBox="0 0 24 24" width="24px" height="24px" xmlns="http://www.w3.org/2000/svg">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg> */}
                X
              </button>
            </div>
          </div>

          <div
            className="fullscreen-modal-header"
            style={{
              marginTop: "16px",
              marginBottom: "10px",
              display: "grid",
              gridTemplateColumns: "1fr auto 1fr",
            }}
          >
            <div className="fullscreen-modal-title">
              <div
                className="fullscreen-source-badge"
                style={{
                  backgroundColor: sourceBadge.bgColor,
                  color: sourceBadge.textColor,
                  borderColor: sourceBadge.borderColor,
                }}
              >
                {sourceBadge.icon}
                <span>
                  {isMobile ? sourceBadge.text : "WorldCC " + sourceBadge.text}
                </span>
              </div>
            </div>
            <div className="fullscreen-metadata">
              <svg
                className="metadata-icon"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
              <span>{metadata}</span>
            </div>
            <button
              className="open-link-full"
              style={{
                display: isFullSourceLinkAvailable ? "flex" : "none",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "bold",
                gap: "8px",
                padding: "8px 16px",
                fontSize: "14px",
                border: "1px solid #ccc",
                backgroundColor: "white",
                borderRadius: "24px",
                cursor: "pointer",
                justifySelf: "end",
              }}
              onClick={() => {
                openLinkInNewTab(linkToOpen);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={isMobile ? "14px" : "16px"}
                height={isMobile ? "14px" : "16px"}
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path
                  fillRule="evenodd"
                  d="M8.636 3.5a.5.5 0 0 0-.5-.5H1.5A1.5 1.5 0 0 0 0 4.5v10A1.5 1.5 0 0 0 1.5 16h10a1.5 1.5 0 0 0 1.5-1.5V7.864a.5.5 0 0 0-1 0V14.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5h6.636a.5.5 0 0 0 .5-.5"
                />
                <path
                  fillRule="evenodd"
                  d="M16 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 1 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0z"
                />
              </svg>
              {isMobile ? "Full Source" : "Open Full Source"}
              {/* Open Full Source */}
            </button>
          </div>

          <div className="fullscreen-modal-body">{modalContent}</div>

          {/* Mobile guidance - enhanced for each content type */}
          {isMobile && (
            <div className="mobile-guidance">
              {fullscreenType === "image" && (
                <span>Pinch to zoom • Tap outside or X to close</span>
              )}
              {fullscreenType === "video" && (
                <span>Tap outside or X to close</span>
              )}
              {fullscreenType === "content" && (
                <span>Scroll to read • Tap outside or X to close</span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render fullscreen video with improved height constraint
  const renderFullscreenVideo = (data: SourceItem) => {
    const url =
      data.content_delta?.source_snippet_link ||
      data.content_delta?.full_source_link;
    const { time } = parseUrl(url);

    return (
      <div className="fullscreen-video-container">
        <ReactPlayer
          url={url}
          ref={playerRef}
          controls={true}
          playing={true}
          data-testid="fullscreen-video"
          width="100%"
          height="100%"
          className="fullscreen-video" // Added for max-height constraint
          onPlay={() => handleReady(time)}
          config={{
            file: {
              attributes: {
                controlsList: "nodownload",
                disablePictureInPicture: true,
              },
            },
          }}
        />
      </div>
    );
  };

  // Render smallscreen video with improved height constraint
  const renderSmallscreenVideo = (data: SourceItem, index: number) => {
    const url =
      data.content_delta?.source_snippet_link ||
      data.content_delta?.full_source_link;
    const { time } = parseUrl(url);

    return (
      <div className="smallscreen-video-container">
        <ReactPlayer
          url={url}
          // ref={playerRef}
          controls={true}
          // playing={false}
          width="100%"
          height="100%"
          data-testid="fullscreen-video"
          className="smallscreen-video" // Added for max-height constraint
          // onPlay={() => handleReady(time)}
          onReady={() => handleReadySmall(playerRef1, time, index)}
          playing={currentlyPlayingIndex === index}
          //  onPlay={() => handlePlayerClick(index)}
          ref={(el) => {
            playerRef1.current[index] = el; // ✅ Assign ref correctly (ensures it returns void)
          }}
          config={{
            file: {
              attributes: {
                controlsList: "nodownload",
                disablePictureInPicture: true,
              },
            },
          }}
        />
      </div>
    );
  };

  // Render fullscreen image with pinch-to-zoom support on mobile
  const renderFullscreenImage = (data: SourceItem) => {
    return (
      <div className="fullscreen-image-container">
        <ZoomableImage
          url={data?.content_delta?.source_snippet_link}
          alt={
            parseSourceTitle(
              data?.content_delta?.title || "",
              data?.content_delta?.source_type
            ).displayTitle || "Source image"
          }
        />
        {/* <img
          src={data?.content_delta?.source_snippet_link}
          alt={
            parseSourceTitle(
              data?.content_delta?.title || "",
              data?.content_delta?.source_type
            ).displayTitle || "Source image"
          }
          className="fullscreen-image"
        /> */}
      </div>
    );
  };

  // Render fullscreen content with improved width constraints
  const renderFullscreenContent = (data: SourceItem) => {
    return (
      <div className="fullscreen-content-container">
        <div className="markdown-styles fullscreen-markdown">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {data?.content_delta?.content || ""}
          </ReactMarkdown>
        </div>
      </div>
    );
  };

  // Render sources panel for desktop with improved close button
  const renderDesktopSourcesPanel = () => {
    // Create a sorted copy of the sources array
    const sortedSources = Array.isArray(sources)
      ? [...sources].sort(
          (a, b) =>
            (a?.content_delta?.number || 0) - (b?.content_delta?.number || 0)
        )
      : [];

    return (
      <div className={`sources-panel ${sourcesOpen ? "open" : ""}`}>
        <div className="sources-panel-header">
          <div className="sources-panel-title">
            <img src={mainlogo} alt="logo" width="20" height="20" />
            <span>Sources</span>
          </div>
          <button
            type="button"
            className="sources-panel-close"
            onClick={closeSourcesPanel}
            aria-label="Close sources panel"
            title="Close"
          >
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="sources-panel-body">
          {sortedSources.map((item, i) => {
            const parsedTitle = parseSourceTitle(
              item?.content_delta?.title || "",
              item?.content_delta?.source_type
            );

            return (
              <div
                className={`source-item ${isExpanded(i) ? "expanded" : ""}`}
                key={`source-${i}`}
              >
                <div className="source-header" onClick={() => toggleSection(i)}>
                  {/* Top row with source number, type badge, metadata */}
                  <div className="header-row">
                    {/* Source number and metadata in one row */}
                    <div className="source-info">
                      <span className="source-number">[{i + 1}]</span>
                      <span className="source-metadata">
                        {getSourceMetadata(item)}
                      </span>
                    </div>

                    {/* Expand button */}
                    <button
                      className={`source-expand-button ${
                        isExpanded(i) ? "expanded" : ""
                      }`}
                      aria-label={
                        isExpanded(i) ? "Collapse source" : "Expand source"
                      }
                      aria-expanded={isExpanded(i)}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSection(i);
                      }}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </button>
                  </div>

                  {/* Title with 2-line display */}
                  <div
                    className="source-title"
                    title={parsedTitle.displayTitle}
                  >
                    {parsedTitle.displayTitle}
                  </div>
                </div>

                {isExpanded(i) && (
                  <div className="source-content">
                    {renderSourceSnippet(item, i)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render sources panel for mobile with consistent close button
  const renderMobileSourcesPanel = () => {
    // Create a sorted copy of the sources array
    const sortedSources = Array.isArray(sources)
      ? [...sources].sort(
          (a, b) =>
            (a?.content_delta?.number || 0) - (b?.content_delta?.number || 0)
        )
      : [];

    return (
      <>
        {/* Overlay for clicking outside on mobile */}
        <div
          className={`mobile-sources-backdrop ${sourcesOpen ? "show" : ""}`}
          onClick={closeSourcesPanel}
          aria-hidden="true"
        ></div>

        {/* Mobile panel */}
        <div className={`mobile-sources-panel ${sourcesOpen ? "open" : ""}`}>
          <div className="mobile-sources-header">
            <div className="mobile-sources-title">
              <img src={mainlogo} alt="logo" width="24" height="24" />
              <span>Sources</span>
            </div>
            <button
              type="button"
              className="mobile-sources-close"
              onClick={closeSourcesPanel}
              aria-label="Close sources panel"
            >
              {/* <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg> */}
              X
            </button>
          </div>

          <div className="mobile-sources-body">
            {sortedSources.map((item, i) => {
              const parsedTitle = parseSourceTitle(
                item?.content_delta?.title || "",
                item?.content_delta?.source_type
              );

              return (
                <div
                  className={`mobile-source-item ${
                    isExpanded(i) ? "expanded" : ""
                  }`}
                  key={`mobile-source-${i}`}
                >
                  <div
                    className="mobile-source-header"
                    onClick={(e) => toggleSection(i, e)}
                  >
                    {/* <div className="source-header" onClick={() => toggleSection(i)}> */}
                    {/* Top row with source number, type badge, metadata */}
                    <div className="header-row">
                      {/* Source number and metadata in one row */}
                      <div className="source-info">
                        <span className="source-number">[{i + 1}]</span>
                        <span className="source-metadata">
                          {getSourceMetadata(item)}
                        </span>
                      </div>

                      {/* Expand button */}
                      <button
                        className={`source-expand-button ${
                          isExpanded(i) ? "expanded" : ""
                        }`}
                        aria-label={
                          isExpanded(i) ? "Collapse source" : "Expand source"
                        }
                        aria-expanded={isExpanded(i)}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSection(i);
                        }}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </button>
                    </div>

                    {/* Title with 2-line display */}
                    <div
                      className="source-title"
                      title={parsedTitle.displayTitle}
                    >
                      {parsedTitle.displayTitle}
                    </div>
                    {/* </div> */}
                  </div>

                  {isExpanded(i) && (
                    <div className="mobile-source-content">
                      {renderSourceSnippet(item, i)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Mobile close instructions */}
          <div className="mobile-close-hint">Tap X or outside to close</div>
        </div>
      </>
    );
  };

  // Render source preview items
  const renderSourcePreviews = () => {
    // Only show first 3 sources in preview - create a sorted copy
    const previewSources = Array.isArray(sources)
      ? [...sources]
          .sort(
            (a, b) =>
              (a?.content_delta?.number || 0) - (b?.content_delta?.number || 0)
          )
          .slice(0, 3)
      : [];

    return (
      <>
        {previewSources.map((item, index) => {
          const sourceBadge = getSourceTypeBadge(
            item?.content_delta?.source_type
          );
          const parsedTitle = parseSourceTitle(
            item?.content_delta?.title || "",
            item?.content_delta?.source_type
          );

          return (
            <div
              className="source-preview"
              key={`preview-${index}`}
              onClick={openSourcePanel}
            >
              <div className="source-preview-content">
                <div
                  style={{
                    display: "flex",
                    alignItems: "end",
                    gap: "8px",
                    color: sourceBadge.textColor,
                    fontSize: "0.75rem",
                    fontWeight: "500",
                    border: sourceBadge.borderColor,
                  }}
                >
                  <div
                    className="source-preview-badge"
                    style={{
                      backgroundColor: sourceBadge.bgColor,
                      color: sourceBadge.textColor,
                      borderColor: sourceBadge.borderColor,
                    }}
                  >
                    {sourceBadge.icon}
                  </div>
                  {isMobile ? sourceBadge.text : "WorldCC " + sourceBadge.text}
                </div>
                <p
                  className="source-preview-title"
                  title={parsedTitle.displayTitle}
                >
                  <span className="source-preview-number">[{index + 1}] </span>
                  {parsedTitle.displayTitle}
                </p>
              </div>
            </div>
          );
        })}
      </>
    );
  };

  // Check if sources is an array and has items
  const hasValidSources = Array.isArray(sources) && sources.length > 0;

  // Main component render function
  return (
    <div className="enhanced-sources-container">
      {/* Fullscreen modal for expanded content */}
      {renderFullscreenModal()}

      {/* Source previews and "Show all" button */}
      <div className="sources-preview-container">
        {renderSourcePreviews()}

        {hasValidSources && (
          <button
            className="show-all-sources-button"
            onClick={openSourcePanel}
            aria-label="Show all sources"
            aria-expanded={sourcesOpen}
          >
            Show all Sources
            <svg
              className="show-all-icon"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        )}
      </div>

      {/* Render appropriate sources panel based on screen size */}
      {isMobile ? renderMobileSourcesPanel() : renderDesktopSourcesPanel()}
    </div>
  );
};

export default EnhancedSources;
